import { ActionCreator, on, ReducerTypes } from "@ngrx/store";
import { PagesStore } from "../pages.state";
import { setLoading, clearLoading, LoadingField } from "./quote.utils";
import { QuoteActions } from "./quote.actions";
import { QuoteVersionActions } from "./version/version.actions";
import { ArticlesActions } from "./articles/articles.actions";
import { VendorInquiriesActions } from "./vendor-inquiries/vendor-inquiries.actions";

// ============================================================================
// LOADING REDUCER FACTORY
// ============================================================================

type AnyAction = ActionCreator<string, (...args: any[]) => any>;

/**
 * Creates a pair of reducers: one to set loading, one to clear it.
 * Eliminates repetitive loading/done handler pairs.
 */
function loadingPair(
    field: LoadingField,
    startActions: AnyAction[],
    endActions: AnyAction[],
): ReducerTypes<PagesStore, readonly AnyAction[]>[] {
    return [
        on(...startActions, (state: PagesStore) => setLoading(state, field)),
        on(...endActions, (state: PagesStore) => clearLoading(state, field)),
    ];
}

// ============================================================================
// LOADING REDUCERS
// ============================================================================

export const loadingReducer = [
    // Dynamic field loading - set loading for the specified field when dispatching with loadingField
    on(QuoteActions.update, (state: PagesStore, { loadingField }) =>
        loadingField ? setLoading(state, loadingField) : state
    ),

    // Quote-level
    ...loadingPair('savingDeliveryAddress',
        [QuoteActions.createDeliveryAddress],
        [QuoteActions.createDeliveryAddressSuccess, QuoteActions.createDeliveryAddressFailure]),
    // Summary panel (all version updates)
    ...loadingPair('summary',
        [
            QuoteVersionActions.updateDiscountDescription, QuoteVersionActions.updateDiscountPercent, QuoteVersionActions.updateDiscountAmount,
            QuoteVersionActions.updateKickbackDescription, QuoteVersionActions.updateKickbackPercent, QuoteVersionActions.updateKickbackAmount,
            QuoteVersionActions.updateManagementDescription, QuoteVersionActions.updateManagementHours, QuoteVersionActions.updateManagementAmount,
            QuoteVersionActions.updateManagementExclude, QuoteVersionActions.updateManagementAutoCalculation,
            QuoteVersionActions.updateSummary, QuoteVersionActions.patchIncludeCustomerPriceDecimals,
        ],
        [
            QuoteVersionActions.updateDiscountSuccess, QuoteVersionActions.updateDiscountFailure,
            QuoteVersionActions.updateKickbackSuccess, QuoteVersionActions.updateKickbackFailure,
            QuoteVersionActions.updateManagementSuccess, QuoteVersionActions.updateManagementFailure,
            QuoteVersionActions.updateSummarySuccess, QuoteVersionActions.updateSummaryFailure,
            QuoteVersionActions.patchIncludeCustomerPriceDecimalsSuccess, QuoteVersionActions.patchIncludeCustomerPriceDecimalsFailure,
        ]),

    ...loadingPair('quoteOptionsLoadAll',
        [QuoteVersionActions.updateQuoteOptions],
        [QuoteVersionActions.updateQuoteOptionsSuccess, QuoteVersionActions.updateQuoteOptionsFailure]),

    // Articles
    ...loadingPair('updatingArticleField',
        [ArticlesActions.updateArticleField],
        [ArticlesActions.updateArticleFieldSuccess, ArticlesActions.updateArticleFieldFailure]),

    ...loadingPair('articles.loading',
        [ArticlesActions.refreshArticles],
        [ArticlesActions.refreshArticlesSuccess, ArticlesActions.refreshArticlesFailure]),

    ...loadingPair('articles.deleting',
        [ArticlesActions.deleteArticle, ArticlesActions.batchDeleteArticles],
        [ArticlesActions.deleteArticleSuccess, ArticlesActions.deleteArticleFailure, ArticlesActions.batchDeleteArticlesSuccess, ArticlesActions.batchDeleteArticlesFailure]),

    ...loadingPair('articles.duplicating',
        [ArticlesActions.duplicateArticle, ArticlesActions.duplicateArticleGroup],
        [ArticlesActions.duplicateArticleSuccess, ArticlesActions.duplicateArticleFailure, ArticlesActions.duplicateArticleGroupSuccess, ArticlesActions.duplicateArticleGroupFailure]),

    ...loadingPair('articles.moving',
        [ArticlesActions.moveArticle],
        [ArticlesActions.moveArticleSuccess, ArticlesActions.moveArticleFailure]),

    ...loadingPair('articles.pasting',
        [ArticlesActions.pasteArticle],
        [ArticlesActions.pasteArticleSuccess, ArticlesActions.pasteArticleFailure]),

    ...loadingPair('articles.uploadingImage',
        [ArticlesActions.uploadArticleImage],
        [ArticlesActions.uploadArticleImageSuccess, ArticlesActions.uploadArticleImageFailure]),

    ...loadingPair('articles.converting',
        [ArticlesActions.convertArticle],
        [ArticlesActions.convertArticleSuccess, ArticlesActions.convertArticleFailure]),

    ...loadingPair('articles.creatingArticle',
        [ArticlesActions.createArticle, ArticlesActions.batchCreateArticles, ArticlesActions.importArticles],
        [ArticlesActions.createArticleSuccess, ArticlesActions.createArticleFailure, ArticlesActions.batchCreateArticlesSuccess, ArticlesActions.batchCreateArticlesFailure, ArticlesActions.importArticlesSuccess, ArticlesActions.importArticlesFailure]),

    ...loadingPair('articles.changingArticles',
        [ArticlesActions.batchChangeCatalogProducts],
        [ArticlesActions.batchChangeCatalogProductsSuccess, ArticlesActions.batchChangeCatalogProductsFailure]),

    // Sales credit
    ...loadingPair('salesCredit',
        [QuoteActions.updateSalesCredit],
        [QuoteActions.updateSalesCreditSuccess, QuoteActions.updateSalesCreditFailure]),

    // Comments
    ...loadingPair('comments.creating',
        [QuoteActions.createComment],
        [QuoteActions.createCommentSuccess, QuoteActions.createCommentFailure]),

    ...loadingPair('comments.deleting',
        [QuoteActions.deleteComment],
        [QuoteActions.deleteCommentSuccess, QuoteActions.deleteCommentFailure]),

    // Vendor Inquiries
    ...loadingPair('vendorInquiries.loading',
        [VendorInquiriesActions.loadVendorInquirySummaries],
        [VendorInquiriesActions.loadVendorInquirySummariesSuccess, VendorInquiriesActions.loadVendorInquirySummariesFailure]),

    ...loadingPair('vendorInquiries.generating',
        [VendorInquiriesActions.generateVendorInquiries],
        [VendorInquiriesActions.generateVendorInquiriesSuccess, VendorInquiriesActions.generateVendorInquiriesFailure]),

    ...loadingPair('vendorInquiries.sending',
        [VendorInquiriesActions.sendVendorInquiries],
        [VendorInquiriesActions.sendVendorInquiriesSuccess, VendorInquiriesActions.sendVendorInquiriesFailure]),

    // Attio sync
    ...loadingPair('syncToAttio',
        [QuoteActions.syncToAttio],
        [QuoteActions.syncToAttioSuccess, QuoteActions.syncToAttioFailure]),
];
